import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TrackRender } from 'app/shared/front-perfomance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  catchError,
  combineLatest,
  debounceTime,
  forkJoin,
  of,
  take,
} from 'rxjs';
import { AuthRepository } from 'app/state/auth.repository';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import { TenantFeatures } from 'app/state/feature.repository';
import { LocalStorageService } from 'app/api/services/local-storage-service';
import { TenantsService } from 'app/state/tenants.service';
import { TenantHomeUpdateModel } from 'app/state/tenants.repository';
import { EventService } from 'app/state/event.service';
import { PersonalTaskService } from 'app/state/personal-task.service';
import { PostsService } from 'app/state/posts.service';
import { ToolBoxesService } from 'app/api/services/toolbox.service';
import { SprintService } from 'app/api/services/sprint.service';
import { NewsUserPageListDto } from 'app/api/models/news/newsUserPageListDto';
import { ToolboxForListDto } from 'app/api/models/toolbox/toolboxForListDto';
import { IEvent } from 'app/api/models/calendar/eventDto';
import { PersonalTaskDto } from 'app/api/models/calendar/personalTaskDto';
import { SprintListDto } from 'app/api/models/sprint/sprintListDto';
import { AgendaItemView } from './dashboard/today-agenda.component';
import { TaskRowView } from './dashboard/tasks-summary.component';
import { LearningProgress } from './dashboard/continue-learning.component';
import {
  SalesProfilePlan,
  SalesProfileService,
} from 'app/api/services/sales-profile.service';
import {
  SalesProfileFinishPayload,
  SalesProfileProgressPayload,
} from 'app/modules/core/components/sales-profile-questionnaire/sales-profile-questionnaire.component';
import {
  scoreSalesProfile,
  SalesProfileAnswers,
  SalesProfileScore,
} from 'app/modules/core/components/sales-profile-questionnaire/sales-profile-scoring';
import { MessageText, ToastService } from 'app/helpers/services/toastService';
import { SalesProfileQuestionnaireService } from 'app/api/services/sales-profile-questionnaire.service';
import { SalesProfileQuestionnaireDto } from 'app/api/models/salesProfile/salesProfileQuestionnaireDto';

@UntilDestroy()
@TrackRender()
@Component({
  selector: 'app-homepage',
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'],
  standalone: false,
})
export class HomepageComponent implements OnInit, AfterViewInit {
  firstName: string | null = null;
  loading = true;

  // Admin-managed questionnaire wording for this user, already resolved to
  // their content language (Danish fallback) by the server. null while it is
  // still loading, or when no language has any copy at all.
  questionnaireContent: SalesProfileQuestionnaireDto | null = null;

  // Set when the tenant has the feature on but no questions stored. The server
  // already drops Questionnaire from the tenant feature list in that case, so
  // this only covers a stale feature list — the page still falls back to the
  // default dashboard rather than showing an empty questionnaire.
  private questionnaireUnavailable = false;

  // How this tenant names the personal-plan feature, resolved the same way the
  // sidebar does so the plan tiles and the menu entry never disagree.
  personalPlanTitleKey = 'personalplan';

  // True when the tenant gates on Billing but the user has no active plan
  // (no plan-granted feature beyond the always-available Billing entry). The
  // home body is then replaced with a paywall lock prompting them to subscribe.
  lockedByPaywall = false;

  // Nybolig gets a launcher-style menu grid instead of the default dashboard,
  // on both mobile and desktop.
  isNybolig = false;
  // Whether the current user already completed the sales-profile questionnaire.
  // When true (or after finishing it) the default dashboard is shown instead —
  // the questionnaire runs only once.
  salesProfileCompleted = false;
  // Gates the questionnaire branch until the "already completed?" check returns,
  // so the questionnaire and the dashboard don't flash before we know which to
  // show.
  salesProfileChecked = false;
  // In-flight guard for the finish POST: prevents double-submit and disables
  // the questionnaire's finish button while saving.
  salesProfileSaving = false;
  // Computed score (track / focus areas / motivations); used as the summary
  // card's fallback when the AI plan couldn't be generated.
  salesProfileScore: SalesProfileScore | null = null;
  // AI-generated plan shown in the summary card; null while generatePlan is in
  // flight (planLoading) or when generation failed (card degrades to the score).
  salesProfilePlan: SalesProfilePlan | null = null;
  planLoading = false;
  // Saved partial progress used to hydrate the questionnaire on resume.
  initialAnswers: string | null = null;
  initialIndex = 0;
  private salesProfileCheckStarted = false;
  // Gates the home body until the active tenant has been matched against the
  // tenant-options list. Without this, the default dashboard (the @else branch)
  // paints for one tick before isNybolig flips true, causing a visible blink of
  // the wrong content on Nybolig (and any tenant-specific layout).
  tenantResolved = false;
  private dashboardLoaded = false;
  // Last resolved tenant id. HomepageComponent is reused across in-place tenant
  // switches, so the run-once questionnaire/dashboard state is reset whenever
  // this changes (see the layout subscription).
  private lastTenantId: string | null = null;

  // Tenant "Information" tab content (headline / pageHtml / video / pageHtml2 /
  // mobile description). Rendered above the grid/dashboard, restoring what the
  // old home page (menu.component) used to show. Loaded per active tenant.
  homeInfo: TenantHomeUpdateModel | null = null;
  videoUrlSafe: SafeResourceUrl | null = null;
  private homeInfoTenantId: string | null = null;

  get hasHomeInfo(): boolean {
    const m = this.homeInfo;
    return !!(
      m &&
      (m.headline ||
        m.pageHtml ||
        m.pageHtml2 ||
        m.descriptionMobile ||
        m.videoUrl ||
        m.logo)
    );
  }

  /** Mobile only shows the headline, video and the dedicated "Mobile
   *  Description" field (pageHtml / pageHtml2 are desktop-only). Used to hide
   *  the whole info card on mobile when none of those are set, so an empty
   *  bordered box isn't left behind. */
  get hasMobileHomeInfo(): boolean {
    const m = this.homeInfo;
    return !!(m && (m.headline || m.descriptionMobile || m.videoUrl || m.logo));
  }

  agenda: AgendaItemView[] = [];
  tasks: TaskRowView[] = [];
  news: NewsUserPageListDto[] = [];
  tools: ToolboxForListDto[] = [];
  learning: LearningProgress | null = null;

  // Features enabled for the active tenant — used to hide dashboard sections
  // (and the matching hero quick-actions) for features the tenant doesn't have.
  enabledFeatures: TenantFeatures[] = [];

  grantedFeatures: TenantFeatures[] = [];

  get hasSprints(): boolean {
    return this.grantedFeatures.includes(TenantFeatures.Sprints);
  }
  get hasToolbox(): boolean {
    return this.grantedFeatures.includes(TenantFeatures.ToolBox);
  }
  get hasNews(): boolean {
    return this.grantedFeatures.includes(TenantFeatures.News);
  }
  get hasCalendar(): boolean {
    return this.grantedFeatures.includes(TenantFeatures.Calendar);
  }
  get hasTodo(): boolean {
    return this.grantedFeatures.includes(TenantFeatures.Todo);
  }
  /** Left column (learning track / toolboxes / news). */
  get showLeftColumn(): boolean {
    return this.hasSprints || this.hasToolbox || this.hasNews;
  }
  /** Right column (today's calendar / other tasks). */
  get showRightColumn(): boolean {
    return this.hasCalendar || this.hasTodo;
  }
  /** The Questionnaire feature swaps the default dashboard for the sales-profile
   *  questionnaire on the home page, shown until it's completed once. It also
   *  requires PersonalPlan, since completing it creates the user's focus areas
   *  which are only visible through the personal-plan module. */
  get hasQuestionnaire(): boolean {
    return (
      this.enabledFeatures.includes(TenantFeatures.Questionnaire) &&
      this.enabledFeatures.includes(TenantFeatures.PersonalPlan)
    );
  }

  /** The feature as the page should act on it: on, and with questions to ask. */
  get questionnaireActive(): boolean {
    return this.hasQuestionnaire && !this.questionnaireUnavailable;
  }

  // Aggregate counts surfaced in the hero chips.
  meetingsCount = 0;
  learningPct = 0;

  private readonly authRepo = inject(AuthRepository);
  private readonly events = inject(EventService);
  private readonly personalTasks = inject(PersonalTaskService);
  private readonly posts = inject(PostsService);
  private readonly toolboxes = inject(ToolBoxesService);
  private readonly sprints = inject(SprintService);
  private readonly generalDataRepo = inject(GeneralDataRepository);
  private readonly storage = inject(LocalStorageService);
  private readonly tenantsService = inject(TenantsService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly salesProfile = inject(SalesProfileService);
  private readonly questionnaire = inject(SalesProfileQuestionnaireService);
  private readonly toast = inject(ToastService);

  ngOnInit(): void {
    this.authRepo.name$.pipe(untilDestroyed(this)).subscribe((name) => {
      if (name) {
        this.firstName = name.split(/\s+/)[0];
      }
    });

    this.generalDataRepo.allGeneralData$
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        const personalPlan = (data.tenantMenuItemsData ?? []).find(
          (x) =>
            x.name?.toLocaleLowerCase() ===
            TenantFeatures.PersonalPlan.toLocaleLowerCase(),
        );
        this.personalPlanTitleKey =
          personalPlan?.customTitle ||
          TenantFeatures.PersonalPlan.toLocaleLowerCase();

        const tenantFeatures = data.features ?? [];
        const planFeatures = data.subscribedFeatures ?? [];
        const billingEnabled = tenantFeatures.includes(TenantFeatures.Billing);
        // Only the Billing entry is auto-granted, so "no other feature" == no active plan.
        const hasPlanAccess = planFeatures.some(
          (f) => f !== TenantFeatures.Billing,
        );
        this.lockedByPaywall = billingEnabled && !hasPlanAccess;
      });

    // The home layout depends on both the active tenant AND its enabled
    // features: the Questionnaire feature swaps the default dashboard for the
    // sales-profile questionnaire. activeTenantFeatures$ is a BehaviorSubject
    // that seeds an empty list and is refreshed right after the tenant options
    // (same synchronous update), so debounceTime(0) collapses that
    // options-then-features burst and lets the decision run once on the settled
    // feature list — a Questionnaire tenant never briefly loads the default
    // dashboard before its features arrive.
    combineLatest([
      this.generalDataRepo.tenantsOptions$,
      this.storage.getTenantId$(),
      this.generalDataRepo.allGeneralData$,
    ])
      .pipe(debounceTime(0), untilDestroyed(this))
      .subscribe(([options, id, data]) => {
        // Home Information content depends only on the active tenant id, not on
        // the tenant-options list — load it before the early-return guard so it
        // isn't skipped on the first tick when options haven't arrived yet.
        if (id) {
          this.loadHomeInfo(id);
        }

        if (!options.length) return;
        const match = options.find((o) => o.value === id);
        const label = (match?.label ?? '').toLowerCase();
        this.isNybolig = label.includes('nybolig');
        this.enabledFeatures = data.features ?? [];
        this.grantedFeatures = data.subscribedFeatures?.length
          ? data.subscribedFeatures
          : this.enabledFeatures;
        this.tenantResolved = true;

        // The component is reused across in-place tenant switches; reset the
        // run-once questionnaire/dashboard state so the new tenant re-resolves
        // cleanly (same-tenant feature re-emits must NOT reset).
        if (id !== this.lastTenantId) {
          this.resetHomeState();
          this.lastTenantId = id;
        }

        // Questionnaire feature: show the questionnaire only until it's
        // completed once; after that (or if already completed) fall back to the
        // default dashboard. Takes precedence over the default dashboard,
        // exactly where the old tenant-label match did.
        if (this.hasQuestionnaire) {
          this.checkSalesProfile();
          return;
        }

        // The default dashboard aggregates several endpoints; skip that work
        // entirely when showing a tenant-specific home layout.
        if (!this.isNybolig && !this.dashboardLoaded) {
          this.dashboardLoaded = true;
          this.loadDashboard();
        }
      });
  }

  // Clears every run-once / questionnaire / plan-popup field so a newly
  // switched-to tenant re-resolves its home layout from scratch.
  private resetHomeState(): void {
    this.salesProfileCheckStarted = false;
    this.salesProfileChecked = false;
    this.salesProfileCompleted = false;
    this.dashboardLoaded = false;
    this.salesProfileSaving = false;
    this.planLoading = false;
    this.salesProfileScore = null;
    this.salesProfilePlan = null;
    this.initialAnswers = null;
    this.initialIndex = 0;
    this.questionnaireContent = null;
    this.questionnaireUnavailable = false;
  }

  private checkSalesProfile(): void {
    if (this.salesProfileCheckStarted) return;
    this.salesProfileCheckStarted = true;
    // The saved answers and the questionnaire wording are both needed before
    // anything can render, so they resolve together — otherwise the card would
    // paint with empty labels for a tick.
    forkJoin({
      saved: this.salesProfile.getMine().pipe(catchError(() => of(null))),
      content: this.questionnaire.getForMe().pipe(catchError(() => of(null))),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ saved: res, content }) => {
        this.questionnaireContent = content;
        this.salesProfileChecked = true;

        if (!content) {
          // Nothing to ask -> act as if the feature were off and show the
          // default dashboard, which the questionnaire branch would otherwise
          // have replaced.
          this.questionnaireUnavailable = true;
          if (!this.dashboardLoaded) {
            this.dashboardLoaded = true;
            this.loadDashboard();
          }
          return;
        }

        if (!res) {
          // No saved row -> fresh questionnaire, no hydration.
          return;
        }
        if (res.isCompleted) {
          // Completed already -> show the persistent "Dit forløb" summary card
          // (never the default dashboard). The plan comes as a nested object; a
          // missing plan just leaves it null and the card renders its header.
          this.salesProfileCompleted = true;
          this.salesProfilePlan = res.plan ?? null;
          return;
        }
        // Partial progress -> resume the questionnaire hydrated at its last spot.
        this.initialAnswers = res.answersJson;
        this.initialIndex = res.currentIndex ?? 0;
      });
  }

  onSalesProfileProgress(payload: SalesProfileProgressPayload): void {
    // Best-effort partial save; never blocks the UI or toasts on failure.
    this.salesProfile
      .save({
        answersJson: payload.answersJson,
        freeText: payload.freeText,
        currentIndex: payload.currentIndex,
        isCompleted: false,
      })
      .pipe(
        catchError(() => of(null)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  onSalesProfileFinish(payload: SalesProfileFinishPayload): void {
    if (this.salesProfileSaving) return;
    this.salesProfileSaving = true;
    this.salesProfile
      .save({
        answersJson: payload.answersJson,
        freeText: payload.freeText,
        currentIndex: this.lastQuestionIndex(payload.answersJson),
        isCompleted: true,
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.salesProfileSaving = false;
          // Only switch to the summary card once the answers are actually saved,
          // so a failed POST keeps the user on the questionnaire (their answers
          // are still in the component) instead of silently losing them.
          this.salesProfileCompleted = true;
          // Unlock the sidebar (and any other gated UI) in real time.
          this.salesProfile.setCompleted(true);
          this.salesProfileScore = this.buildSalesProfileScore(payload);
          if (this.salesProfileScore) {
            this.generateSalesProfilePlan(this.salesProfileScore);
          }
        },
        error: () => {
          this.salesProfileSaving = false;
          this.toast.showError(MessageText.error);
        },
      });
  }

  private generateSalesProfilePlan(score: SalesProfileScore): void {
    // The summary card is already shown with a loading state; the AI plan
    // streams into it.
    this.salesProfilePlan = null;
    this.planLoading = true;
    this.salesProfile
      .generatePlan({
        track: score.track,
        trackLabel: score.trackLabel,
        focusAreas: score.focusAreas,
        motivations: score.motivations,
        freeText: score.freeText || null,
      })
      .pipe(
        // Plan generation failing must not strand the user: the card falls back
        // to the structured score (track + focus-area names) with no AI copy.
        catchError(() => of(null)),
        untilDestroyed(this),
      )
      .subscribe((plan) => {
        this.salesProfilePlan = plan;
        this.planLoading = false;
      });
  }

  private lastQuestionIndex(answersJson: string): number {
    try {
      const parsed = JSON.parse(answersJson) as { answers?: unknown[] };
      const len = Array.isArray(parsed?.answers) ? parsed.answers.length : 0;
      return Math.max(0, len - 1);
    } catch {
      return 0;
    }
  }

  private buildSalesProfileScore(
    payload: SalesProfileFinishPayload,
  ): SalesProfileScore | null {
    try {
      const answers = JSON.parse(payload.answersJson) as SalesProfileAnswers;
      return scoreSalesProfile(answers, payload.freeText);
    } catch {
      return null;
    }
  }

  onSalesProfileReset(): void {
    // Clears the saved profile/plan on the backend, then drops local state back
    // to a fresh questionnaire. The summary card gates this behind a confirm.
    let failed = false;
    this.salesProfile
      .reset()
      .pipe(
        catchError(() => {
          failed = true;
          this.toast.showError(MessageText.error);
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (failed) return;
        this.salesProfileCompleted = false;
        // Re-lock the sidebar (and any other gated UI) in real time.
        this.salesProfile.setCompleted(false);
        this.salesProfilePlan = null;
        this.salesProfileScore = null;
        this.initialAnswers = null;
        this.initialIndex = 0;
        this.salesProfileCheckStarted = false;
        this.salesProfileChecked = true;
      });
  }

  private loadHomeInfo(tenantId: string): void {
    if (this.homeInfoTenantId === tenantId) return;
    this.homeInfoTenantId = tenantId;
    this.tenantsService
      .loadTenantHomePageModel(tenantId)
      .pipe(
        catchError(() => of(null)),
        untilDestroyed(this),
      )
      .subscribe((model) => {
        this.homeInfo = model;
        this.videoUrlSafe = model?.videoUrl
          ? this.sanitizer.bypassSecurityTrustResourceUrl(model.videoUrl)
          : null;
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      console.log('APP_READY_HomePage');
    }, 1000);
  }

  private loadDashboard(): void {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    forkJoin({
      events: this.hasCalendar
        ? this.events
            .getByDate(dateStr)
            .pipe(catchError(() => of([] as IEvent[])))
        : of([] as IEvent[]),
      tasks: this.hasTodo
        ? this.personalTasks
            .getMyTasks()
            .pipe(catchError(() => of([] as PersonalTaskDto[])))
        : of([] as PersonalTaskDto[]),
      news: this.hasNews
        ? this.posts
            .getNewsUserPage()
            .pipe(catchError(() => of([] as NewsUserPageListDto[])))
        : of([] as NewsUserPageListDto[]),
      tools: this.hasToolbox
        ? this.toolboxes
            .loadAllForUser()
            .pipe(catchError(() => of([] as ToolboxForListDto[])))
        : of([] as ToolboxForListDto[]),
      sprints: this.hasSprints
        ? this.sprints
            .loadAllForUser()
            .pipe(catchError(() => of([] as SprintListDto[])))
        : of([] as SprintListDto[]),
    })
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: ({ events, tasks, news, tools, sprints }) => {
          this.agenda = this.mapAgenda(events);
          this.tasks = this.mapTasks(tasks);
          this.news = news ?? [];
          this.tools = tools ?? [];
          this.learning = this.mapLearning(sprints);
          this.meetingsCount = this.agenda.length;
          this.learningPct = this.learning?.percentComplete ?? 0;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private mapAgenda(events: IEvent[]): AgendaItemView[] {
    if (!Array.isArray(events)) return [];
    const rows = events.map((e) => {
      const start = new Date(e.startDate ?? e.eventDate);
      const valid = !Number.isNaN(start.getTime());
      return {
        ts: valid ? start.getTime() : 0,
        view: {
          id: String(e.id ?? ''),
          time: valid
            ? start.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })
            : '',
          title: String(e.title ?? ''),
          contact: e.crmContactName ?? e.crmAccountName ?? null,
          status: 'in' as const,
          value: null,
        } satisfies AgendaItemView,
      };
    });
    rows.sort((a, b) => a.ts - b.ts);
    return rows.map((r) => r.view);
  }

  private mapTasks(tasks: PersonalTaskDto[]): TaskRowView[] {
    if (!Array.isArray(tasks)) return [];
    const open = tasks.filter((t) => !t.isCompleted);
    if (open.length === 0) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayCount = open.filter((t) => {
      const due = new Date(t.dueDate);
      return (
        !Number.isNaN(due.getTime()) &&
        due.getTime() <= today.getTime() + 24 * 60 * 60 * 1000
      );
    }).length;
    const upcomingCount = open.length - todayCount;

    const rows: TaskRowView[] = [];
    if (todayCount > 0) {
      rows.push({
        kind: 'today',
        labelKey: 'dashTaskToday',
        count: todayCount,
        subKey: 'dashTaskTodaySub',
        icon: 'check',
        accent: 'var(--igntr-accent)',
        link: '/todo',
      });
    }
    if (upcomingCount > 0) {
      rows.push({
        kind: 'upcoming',
        labelKey: 'dashTaskUpcoming',
        count: upcomingCount,
        subKey: 'dashTaskUpcomingSub',
        icon: 'calendar',
        accent: 'oklch(0.62 0.16 160)',
        link: '/todo',
      });
    }
    return rows;
  }

  private mapLearning(sprints: SprintListDto[]): LearningProgress | null {
    if (!Array.isArray(sprints) || sprints.length === 0) return null;

    const myActive = sprints
      .filter((s) => s.isActive && Array.isArray(s.participants))
      .map((s) => {
        const me = s.participants.find((p) => p.isCurrentUser);
        return me ? { sprint: s, progression: me.progression ?? -1 } : null;
      })
      .filter(
        (x): x is { sprint: SprintListDto; progression: number } => x !== null,
      );

    if (myActive.length === 0) return null;

    // Prefer an in-progress sprint (started but not finished), else the most recently updated one.
    const inProgress = myActive.find(
      (x) => x.progression >= 0 && x.progression < x.sprint.modulesCount - 1,
    );
    const pick = inProgress ?? myActive[0];

    // progression matches sprint-pipeline-view semantics: -1 = not started,
    // 0..N-2 = in progress (index of current module), N-1 = completed.
    const total = Math.max(1, pick.sprint.modulesCount ?? 0);
    const prog = pick.progression;
    const isComplete = prog >= total - 1;
    const currentStep = Math.min(Math.max(0, prog), total - 1);
    const completedCount = isComplete ? total : Math.max(0, prog);
    const percentComplete = Math.min(
      100,
      Math.round((completedCount / total) * 100),
    );

    return {
      title: '',
      totalSteps: total,
      currentStep,
      currentStepTitle: String(pick.sprint.title ?? ''),
      resumeLink: `/sprints/${pick.sprint.id}`,
      percentComplete,
    };
  }
}
